/**
 * VoiceDashboard - Main Parent Component
 * 
 * This component serves as the main container for the Voice Dashboard module.
 * It provides a tabbed interface to navigate between different dashboard views.
 */

import React, { useState, lazy, Suspense } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import FuseLoading from '@fuse/core/FuseLoading';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CampaignIcon from '@mui/icons-material/Campaign';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import DialpadIcon from '@mui/icons-material/Dialpad';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SummarizeIcon from '@mui/icons-material/Summarize';

// Lazy load tab components for better performance
const UserCampaignsTab = lazy(() => import('./tabs/UserCampaignsTab'));
const VoiceDashboardTab = lazy(() => import('./tabs/VoiceDashboardTab'));
const VpbxDashboardTab = lazy(() => import('./tabs/VpbxDashboardTab'));
const VpbxWallboardTab = lazy(() => import('./tabs/VpbxWallboardTab'));
const ReportsTab = lazy(() => import('./tabs/ReportsTab'));
const CampaignSummaryTab = lazy(() => import('./tabs/CampaignSummaryTab'));

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%'
	},
	headerContainer: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		padding: theme.spacing(0, 2)
	},
	headerIcon: {
		marginRight: theme.spacing(1.5)
	},
	headerTitle: {
		fontSize: '18px',
		fontWeight: 600,
		color: theme.palette.text.primary
	},
	contentContainer: {
		padding: theme.spacing(2)
	},
	tabsContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: 0,
		backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#F3F4F6',
		borderRadius: 12,
		padding: 4,
		marginBottom: theme.spacing(3),
		width: 'fit-content',
		flexWrap: 'wrap'
	},
	tab: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1.25, 3),
		borderRadius: 10,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: 500,
		color: theme.palette.text.secondary,
		backgroundColor: 'transparent',
		border: 'none',
		transition: 'all 0.2s ease',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
		}
	},
	activeTab: {
		backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
		color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
		boxShadow: theme.palette.type === 'dark'
			? '0 2px 8px rgba(0,0,0,0.3)'
			: '0 2px 8px rgba(0,0,0,0.08)',
		fontWeight: 600
	},
	tabIcon: {
		fontSize: 20
	},
	tabContent: {
		minHeight: 400
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 400
	}
}));

/**
 * Tab configuration
 */
function VoicePortal({ data = null }) {
	const TABS = [
		{
			id: 'voice-dashboard',
			label: 'Voice Dashboard',
			icon: PhoneInTalkIcon,
			component: VoiceDashboardTab
		},
		{
			id: 'vpbx-dashboard',
			label: 'Call Center Dashboard',
			icon: DialpadIcon,
			component: VpbxDashboardTab
		},
		{
			id: 'vpbx-wallboard',
			label: 'Call Center Wallboard',
			icon: ViewQuiltIcon,
			component: VpbxWallboardTab
		},
		{
			id: 'reports',
			label: 'Reports',
			icon: AssessmentIcon,
			component: ReportsTab
		},
	];

	const classes = useStyles();
	const theme = useTheme();

	// State for tracking active tab
	const [activeTab, setActiveTab] = useState(TABS[0]?.id || 'user-campaigns');

	// Render active tab content
	const renderTabContent = () => {
		const currentTab = TABS.find(tab => tab.id === activeTab);
		if (!currentTab) return null;

		const TabComponent = currentTab.component;
		return (
			<Suspense
				fallback={
					<div className={classes.loadingContainer}>
						<FuseLoading />
					</div>
				}
			>
				<TabComponent userData={data} />
			</Suspense>
		);
	};

	return (
		<FusePageSimple
			header={
				<div className={classes.headerContainer}>
					<FuseAnimate animation="transition.expandIn" delay={300}>
						<DashboardIcon fontSize="large" className={classes.headerIcon} />
					</FuseAnimate>
					<FuseAnimate animation="transition.slideLeftIn" delay={300}>
						<Typography variant="h6" className={classes.headerTitle}>
							Voice Portal
						</Typography>
					</FuseAnimate>
				</div>
			}
			content={
				<div className={classes.contentContainer}>
					{/* Tabs */}
					<div className={classes.tabsContainer}>
						{TABS.map((tab) => {
							const IconComponent = tab.icon;
							return (
								<button
									key={tab.id}
									className={`${classes.tab} ${activeTab === tab.id ? classes.activeTab : ''}`}
									onClick={() => setActiveTab(tab.id)}
								>
									<IconComponent className={classes.tabIcon} />
									{tab.label}
								</button>
							);
						})}
					</div>

					{/* Tab Content */}
					<div className={classes.tabContent}>
						{renderTabContent()}
					</div>
				</div>
			}
		/>
	);
}

export default VoicePortal;